import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import { ENDPOINTS } from "../../services/endpoints";
import changePasswordConfig from "../../config/forms/changepassword.json";
import { IMAGES } from "../../constants/image";
import { validateValue } from "../../utils/validation";
import { useAuth } from "../../context/AuthContext";
import usePopup from "../../hooks/usePopup";
import { extractMessageResponse } from "../../utils/messageResponse";

interface FieldValidation {
  label?: string;
  required?: boolean;
  pattern?: string;
  minLength?: number;
  maxLength?: number;
  matchField?: string;
}

interface FormField {
  name: string;
  type: string;
  label?: string;
  placeholder?: string;
  icon?: string;
  validation?: FieldValidation;
}

const getApiMessage = (value: any, fallback: string): string => {
  if (!value) return fallback;
  if (typeof value === "string") return value;
  if (Array.isArray(value)) return value.filter(Boolean).join(" ") || fallback;
  if (typeof value === "object") {
    return (
      Object.values(value)
        .map((item) => getApiMessage(item, ""))
        .filter(Boolean)
        .join(" ") || fallback
    );
  }
  return String(value);
};

const getChangePasswordErrorMessage = (value: any): string => {
  const message = getApiMessage(value, "Unable to change password");

  if (/unauthenticated/i.test(message)) {
    return "Please login again";
  }

  if (/old password|current password|invalid|incorrect/i.test(message)) {
    return "Incorrect old password";
  }

  return message;
};

const PASSWORD_RULES = [
  {
    label: "At least 8 characters",
    test: (value: string) => value.length >= 8,
  },
  {
    label: "At least 1 uppercase letter",
    test: (value: string) => /[A-Z]/.test(value),
  },
  {
    label: "At least 1 lowercase letter",
    test: (value: string) => /[a-z]/.test(value),
  },
  {
    label: "At least 1 number",
    test: (value: string) => /\d/.test(value),
  },
  {
    label: "At least 1 special character",
    test: (value: string) => /[^A-Za-z0-9\s]/.test(value),
  },
  {
    label: "No spaces",
    test: (value: string) => !/\s/.test(value),
  },
];

const ChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const showPopup = usePopup();
  const iconMap: Record<string, string> = {
    old_password: IMAGES.CHANGEPASSWORD.PASSWORD_ICON,
    new_password: IMAGES.CHANGEPASSWORD.PASSWORD_ICON,
    confirm_password: IMAGES.CHANGEPASSWORD.PASSWORD_ICON,
  };
  const toggleIconMap: Record<string, string> = {
    old_password: IMAGES.CHANGEPASSWORD.EYE_ICON,
    new_password: IMAGES.CHANGEPASSWORD.EYE_ICON,
    confirm_password: IMAGES.CHANGEPASSWORD.EYE_ICON,
  };

  const [formData, setFormData] = useState<any>({});
  const [showPassword, setShowPassword] = useState<any>({});
  const [errors, setErrors] = useState<any>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [activeInfoField, setActiveInfoField] = useState<string | null>(null);

  const fields = changePasswordConfig.fields as FormField[];

  const validateField = (name: string, value: string, data = formData) => {
    const field = fields.find((item) => item.name === name);
    if (!field) return "";

    return (
      validateValue(
        value,
        {
          label: field.label,
          ...(field.validation || {}),
        },
        data,
      ) || ""
    );
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const nextFormData = { ...formData, [name]: value };
    setFormData(nextFormData);
    setErrors((prev: any) => ({
      ...prev,
      [name]:
        touched[name] || prev[name]
          ? validateField(name, value, nextFormData)
          : "",
      ...(name === "new_password" &&
      nextFormData.confirm_password &&
      (touched.confirm_password || prev.confirm_password)
        ? {
            confirm_password: validateField(
              "confirm_password",
              nextFormData.confirm_password,
              nextFormData,
            ),
          }
        : {}),
    }));
  };

  const handleBlur = (name: string) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev: any) => ({
      ...prev,
      [name]: validateField(name, formData[name] || ""),
    }));
  };

  const togglePassword = (fieldName: string) => {
    setShowPassword((prev: any) => ({
      ...prev,
      [fieldName]: !prev[fieldName],
    }));
  };

  const renderPasswordInfo = (fieldName: string) => {
    if (fieldName !== "new_password" && fieldName !== "confirm_password") {
      return null;
    }

    const passwordValue = String(formData.new_password || "");
    const confirmValue = String(formData.confirm_password || "");
    const rules =
      fieldName === "confirm_password"
        ? [
            {
              label: "Matches the new password",
              valid: Boolean(confirmValue) && confirmValue === passwordValue,
            },
          ]
        : PASSWORD_RULES.map((rule) => ({
            label: rule.label,
            valid: rule.test(passwordValue),
          }));

    return (
      <span
        className={`password-info ${activeInfoField === fieldName ? "is-open" : ""}`}
      >
        <button
          type="button"
          className="password-info-trigger"
          aria-label={`${fieldName === "confirm_password" ? "Confirm password" : "Password"} rules`}
          aria-expanded={activeInfoField === fieldName}
          onClick={() =>
            setActiveInfoField((current) =>
              current === fieldName ? null : fieldName,
            )
          }
          onBlur={() => {
            window.setTimeout(() => setActiveInfoField(null), 120);
          }}
        >
          i
        </button>
        <span className="password-info-popover" role="tooltip">
          {rules.map((rule) => (
            <span
              className={`password-rule ${
                rule.valid ? "is-valid" : "is-invalid"
              }`}
              key={rule.label}
            >
              <span className="password-rule-icon">
                {rule.valid ? "✓" : "×"}
              </span>
              {rule.label}
            </span>
          ))}
        </span>
      </span>
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};
    fields.forEach((field) => {
      newErrors[field.name] = validateField(
        field.name,
        formData[field.name] || "",
      );
    });

    setErrors(newErrors);
    setTouched(
      fields.reduce<Record<string, boolean>>((next, field) => {
        next[field.name] = true;
        return next;
      }, {}),
    );
    if (Object.values(newErrors).some((err) => Boolean(err))) return;

    const payload = {
      old_password: formData.old_password,
      new_password: formData.new_password,
      confirm_password: formData.confirm_password,
      new_password_confirmation: formData.confirm_password,
    };

    try {
      setIsSubmitting(true);
      const response = await api.post(ENDPOINTS.AUTH.CHANGE_PASSWORD, payload);
      const apiData = response?.data;
      const messageText =
        typeof apiData?.message === "string" ? apiData.message : "";
      const messageResponse = extractMessageResponse(apiData, "Password changed successfully", 200);
      const responseError =
        apiData?.error ||
        apiData?.errors ||
        apiData?.message?.old_password ||
        apiData?.message?.new_password ||
        apiData?.message?.confirm_password ||
        apiData?.message?.error ||
        (/unauthenticated|old password|current password|invalid|incorrect/i.test(
          messageText,
        )
          ? messageText
          : "") ||
        (apiData?.status === false ? apiData?.message : "");

      const isErrorResponse =
        Boolean(responseError) ||
        messageResponse.messageType === "E" ||
        messageResponse.messageCode?.startsWith("E") ||
        messageResponse.status >= 400;

      if (isErrorResponse) {
        const popupMessage =
          messageResponse.message &&
          (messageResponse.messageType === "E" ||
            messageResponse.messageCode?.startsWith("E") ||
            messageResponse.status >= 400)
            ? messageResponse.message
            : getChangePasswordErrorMessage(responseError || messageText);

        showPopup({
          status: messageResponse.status,
          message: popupMessage,
          messageType: messageResponse.messageType,
          messageCode: messageResponse.messageCode,
        });
        return;
      }

      const successMessage = messageResponse.message;
      showPopup({
        status: messageResponse.status,
        message: successMessage,
        messageType: messageResponse.messageType,
        messageCode: messageResponse.messageCode,
        onOk: () => {
          logout();
          navigate("/login");
        },
      });
      setFormData({});
      setErrors({});
      setTouched({});
    } catch (error: any) {
      const apiData = error?.response?.data;
      const messageResponse = extractMessageResponse(apiData, "Failed to change password", 500);
      const message =
        apiData?.error?.[0] ||
        apiData?.message?.old_password?.[0] ||
        apiData?.message?.new_password?.[0] ||
        apiData?.message?.confirm_password?.[0] ||
        apiData?.message ||
        "Failed to change password";
      const popupMessage =
        messageResponse.message &&
        (messageResponse.messageType === "E" ||
          messageResponse.messageCode?.startsWith("E") ||
          messageResponse.status >= 400)
          ? messageResponse.message
          : getChangePasswordErrorMessage(message);
      showPopup({
        status: messageResponse.status,
        message: popupMessage,
        messageType: messageResponse.messageType,
        messageCode: messageResponse.messageCode,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="reset-wrapper"
      // style={{ backgroundImage: `url(${bgImage})` }}
    >
      <div className="reset-background">
        <div className="reset-card-container">
          <div className="card-body">
            <h3 className="text-center fw-bold mb-1 text-white">
              Change Password
            </h3>
            <p className="text-center text-white mb-1">
              *All Fields are Mandatory
            </p>

            <form onSubmit={handleSubmit} className="change-password-form">
              {fields.map((field) => (
                <div className="mb-1" key={field.name}>
                  <label className="form-label text-white password-field-label">
                    <span>{field.label}</span>
                    {renderPasswordInfo(field.name)}
                  </label>
                  <div className="change-password-input-wrap">
                    <input
                      type={
                        field.type === "password"
                          ? showPassword[field.name]
                            ? "text"
                            : "password"
                          : field.type
                      }
                      name={field.name}
                      className={`form-control custom-input ${
                        errors[field.name] ? "input-error" : ""
                      }`}
                      placeholder={field.placeholder}
                      value={formData[field.name] || ""}
                      onChange={handleChange}
                      onBlur={() => handleBlur(field.name)}
                    />

                    {iconMap[field.name] && (
                      <img
                        src={iconMap[field.name]}
                        alt={`${field.name}-icon`}
                        className="input-icon"
                      />
                    )}

                    {field.type === "password" && (
                      <img
                        src={
                          showPassword[field.name]
                            ? toggleIconMap[field.name]
                            : IMAGES.CHANGEPASSWORD.EYE_HIDE_ICON
                        }
                        alt={
                          showPassword[field.name]
                            ? "show password"
                            : "Hide password"
                        }
                        className="password-toggle"
                        onClick={() => togglePassword(field.name)}
                      />
                    )}
                  </div>

                  <small className="error-text">{errors[field.name]}</small>
                </div>
              ))}

              <button
                type="submit"
                className="btn btn-primary w-100"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Processing..." : "Change Password"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
